// src/components/Admin/sections/NotificationsDetailsView.jsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  IconButton,
  Avatar,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Campaign as CampaignIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import client from '../../../../../API';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2, 2, 0, 0),
}));

// ============================================================================
// CONSTANTS
// ============================================================================
const NOTIFICATION_TYPES = [
  { value: 'system_announcement', label: 'System Announcement', icon: '📢' },
  { value: 'system_update', label: 'System Update', icon: '🔄' },
  { value: 'campaign_created', label: 'Campaign Created', icon: '📋' },
  { value: 'campaign_invitation', label: 'Campaign Invitation', icon: '✉️' },
  { value: 'partnership_invitation', label: 'Partnership Invitation', icon: '🤝' },
  { value: 'new_message', label: 'New Message', icon: '💬' },
  { value: 'payout_completed', label: 'Payout Completed', icon: '💰' },
  { value: 'invoice_paid', label: 'Invoice Paid', icon: '💳' },
  { value: 'contract_pending', label: 'Contract Pending', icon: '📄' },
  { value: 'organizer_shared', label: 'Organizer Shared', icon: '📁' },
];

const NOTIFICATION_SOURCES = [
  { value: 'system', label: 'System', color: 'default' },
  { value: 'campaign', label: 'Campaign', color: 'primary' },
  { value: 'partnership', label: 'Partnership', color: 'secondary' },
  { value: 'conversation', label: 'Conversation', color: 'info' },
  { value: 'financial', label: 'Financial', color: 'success' },
  { value: 'contract', label: 'Contract', color: 'warning' },
  { value: 'organizer', label: 'Organizer', color: 'default' },
];

const PRIORITY_LEVELS = [
  { value: 'low', label: 'Low', color: 'default', icon: <InfoIcon /> },
  { value: 'medium', label: 'Medium', color: 'primary', icon: <NotificationsIcon /> },
  { value: 'high', label: 'High', color: 'warning', icon: <WarningIcon /> },
  { value: 'urgent', label: 'Urgent', color: 'error', icon: <ErrorIcon /> },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const NotificationsDetailsView = () => {
  const queryClient = useQueryClient();

  // ──────────────────────────────────────────────────────────────────────
  // STATE
  // ──────────────────────────────────────────────────────────────────────
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [notificationType, setNotificationType] = useState('system_announcement');
  const [notificationSource, setNotificationSource] = useState('system');
  const [priority, setPriority] = useState('medium');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [url, setUrl] = useState('');
  const [sendPush, setSendPush] = useState(true);

  // Optional context fields
  const [campaignId, setCampaignId] = useState('');
  const [partnershipId, setPartnershipId] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [organizerId, setOrganizerId] = useState('');

  // Preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ──────────────────────────────────────────────────────────────────────
  // USER SEARCH
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (userSearch.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await client.globalUsers.search({
          q: userSearch,
          limit: 20,
        });
        const users = response?.users || [];
        setSearchResults(users);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [userSearch]);

  // ──────────────────────────────────────────────────────────────────────
  // CREATE NOTIFICATION MUTATION
  // ──────────────────────────────────────────────────────────────────────
  const createNotificationMutation = useMutation(
    (payload) => client.notifications.create(payload),
    {
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          message: data?.message || 'Notifications sent successfully!',
          severity: 'success',
        });
        
        // Clear form
        setSelectedUsers([]);
        setTitle('');
        setMessage('');
        setUrl('');
        setCampaignId('');
        setPartnershipId('');
        setConversationId('');
        setOrganizerId('');
        setPreviewOpen(false);
        
        queryClient.invalidateQueries(['notifications']);
      },
      onError: (error) => {
        console.error('Error creating notification:', error);
        const errorMessage = error.response?.data?.error || 'Failed to send notifications';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      },
    }
  );

  // ──────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ──────────────────────────────────────────────────────────────────────
  const handlePreview = () => {
    if (!validateForm()) return;
    setPreviewOpen(true);
  };

  const validateForm = () => {
    if (selectedUsers.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one user',
        severity: 'error',
      });
      return false;
    }

    if (!title.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a notification title',
        severity: 'error',
      });
      return false;
    }

    return true;
  };

  const handleSendNotification = () => {
    const payload = {
      global_user_ids: selectedUsers.map(u => u.id),
      notification_type: notificationType,
      notification_source: notificationSource,
      title: title.trim(),
      message: message.trim() || undefined,
      priority: priority,
      send_push: sendPush,
    };

    // Add optional fields
    if (url.trim()) payload.frontend_route = url.trim();
    if (campaignId.trim()) payload.campaign_id = parseInt(campaignId);
    if (partnershipId.trim()) payload.partnership_id = parseInt(partnershipId);
    if (conversationId.trim()) payload.conversation_id = parseInt(conversationId);
    if (organizerId.trim()) payload.organizer_id = parseInt(organizerId);

    createNotificationMutation.mutate(payload);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const getUserTypeLabel = (user) => {
    if (user.user_type === 'creator') return 'Creator';
    if (user.user_type === 'normal') return 'Brand/Agency';
    return user.user_type;
  };

  const getUserDisplayName = (user) => {
    return user.username || user.email || `User ${user.id}`;
  };

  // ──────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <StyledCard sx={{ mb: 3 }}>
        <HeaderBox>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'rgba(255,255,255,0.2)',
              }}
            >
              <NotificationsIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Admin Notifications
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                Send notifications to global users across the platform
              </Typography>
            </Box>
          </Box>
        </HeaderBox>
      </StyledCard>

      <Grid container spacing={3}>
        {/* Left Column - Form */}
        <Grid item xs={12} md={7}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Create Notification
              </Typography>

              {/* User Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Recipients *
                </Typography>
                
                <Autocomplete
                  multiple
                  value={selectedUsers}
                  onChange={(event, newValue) => setSelectedUsers(newValue)}
                  inputValue={userSearch}
                  onInputChange={(event, newInputValue) => setUserSearch(newInputValue)}
                  options={searchResults}
                  loading={searching}
                  getOptionLabel={(option) => getUserDisplayName(option)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getUserDisplayName(option)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getUserTypeLabel(option)} • ID: {option.id}
                          {option.email && ` • ${option.email}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getUserDisplayName(option)}
                        size="small"
                        avatar={
                          <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24 }}>
                            <PersonIcon sx={{ fontSize: 14 }} />
                          </Avatar>
                        }
                        onDelete={() => handleRemoveUser(option.id)}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedUsers.length === 0 ? "Search by username, email, or ID..." : ""}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText={userSearch.length < 2 ? "Type to search users..." : "No users found"}
                  sx={{ mb: 2 }}
                />

                {selectedUsers.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                  </Alert>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Notification Type & Source */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Notification Type *</InputLabel>
                    <Select
                      value={notificationType}
                      label="Notification Type *"
                      onChange={(e) => setNotificationType(e.target.value)}
                    >
                      {NOTIFICATION_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{type.icon}</span>
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Source Category *</InputLabel>
                    <Select
                      value={notificationSource}
                      label="Source Category *"
                      onChange={(e) => setNotificationSource(e.target.value)}
                    >
                      {NOTIFICATION_SOURCES.map((source) => (
                        <MenuItem key={source.value} value={source.value}>
                          <Chip
                            label={source.label}
                            size="small"
                            color={source.color}
                            variant="outlined"
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Priority */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Priority Level *</InputLabel>
                <Select
                  value={priority}
                  label="Priority Level *"
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {PRIORITY_LEVELS.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {level.icon}
                        <Chip
                          label={level.label}
                          size="small"
                          color={level.color}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Title */}
              <TextField
                label="Notification Title"
                fullWidth
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 3 }}
                helperText="Keep it concise and clear"
              />

              {/* Message */}
              <TextField
                label="Message"
                fullWidth
                multiline
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mb: 3 }}
                helperText="Optional detailed message"
              />

              {/* URL */}
              <TextField
                label="Link URL (Frontend Route)"
                fullWidth
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="/dashboard or /campaigns/123"
                sx={{ mb: 3 }}
                helperText="Where should users go when they click? (optional)"
              />

              <Divider sx={{ my: 3 }} />

              {/* Optional Context Fields */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Optional Context (for linking)
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Campaign ID"
                    fullWidth
                    size="small"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    type="number"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Partnership ID"
                    fullWidth
                    size="small"
                    value={partnershipId}
                    onChange={(e) => setPartnershipId(e.target.value)}
                    type="number"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Conversation ID"
                    fullWidth
                    size="small"
                    value={conversationId}
                    onChange={(e) => setConversationId(e.target.value)}
                    type="number"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Organizer ID"
                    fullWidth
                    size="small"
                    value={organizerId}
                    onChange={(e) => setOrganizerId(e.target.value)}
                    type="number"
                  />
                </Grid>
              </Grid>

              {/* Push Notification Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={sendPush}
                    onChange={(e) => setSendPush(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Send Push Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Notify users on their mobile devices
                    </Typography>
                  </Box>
                }
              />

              {/* Action Buttons */}
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handlePreview}
                  startIcon={<PreviewIcon />}
                  disabled={createNotificationMutation.isLoading}
                  fullWidth
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSendNotification}
                  startIcon={
                    createNotificationMutation.isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  disabled={createNotificationMutation.isLoading || selectedUsers.length === 0}
                  fullWidth
                >
                  {createNotificationMutation.isLoading
                    ? 'Sending...'
                    : `Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Right Column - Quick Tips & Stats */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            {/* Quick Tips */}
            <StyledCard>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  💡 Quick Tips
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Search Global Users
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Search works for both creators and brand/agency users by username, email, or ID
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Priority Levels
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Urgent = Red badge, High = Orange, Medium = Blue, Low = Gray
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Push Notifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, users receive instant alerts on their iOS devices
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Context IDs
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Link notifications to specific campaigns, partnerships, etc.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>

            {/* Selected Users Preview */}
            {selectedUsers.length > 0 && (
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Recipients ({selectedUsers.length})
                  </Typography>
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {selectedUsers.map((user) => (
                      <ListItem
                        key={user.id}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveUser(user.id)}
                          >
                            <CloseIcon />
                          </IconButton>
                        }
                        sx={{ px: 0 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={getUserDisplayName(user)}
                          secondary={
                            <>
                              {getUserTypeLabel(user)}
                              {user.email && ` • ${user.email}`}
                            </>
                          }
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </StyledCard>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon />
            Notification Preview
          </Box>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={NOTIFICATION_TYPES.find(t => t.value === notificationType)?.label}
                    size="small"
                    color="primary"
                  />
                  <Chip
                    label={PRIORITY_LEVELS.find(p => p.value === priority)?.label}
                    size="small"
                    color={PRIORITY_LEVELS.find(p => p.value === priority)?.color}
                  />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {title || '(No title)'}
                </Typography>
                {message && (
                  <Typography variant="body2" color="text.secondary">
                    {message}
                  </Typography>
                )}
                {url && (
                  <Typography variant="caption" color="primary">
                    → {url}
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Will be sent to:
              </Typography>
              <Stack spacing={0.5}>
                {selectedUsers.slice(0, 5).map((user) => (
                  <Typography key={user.id} variant="body2">
                    • {getUserDisplayName(user)} ({getUserTypeLabel(user)})
                  </Typography>
                ))}
                {selectedUsers.length > 5 && (
                  <Typography variant="body2" color="text.secondary">
                    ... and {selectedUsers.length - 5} more
                  </Typography>
                )}
              </Stack>
            </Box>

            {sendPush && (
              <Alert severity="info" icon={<NotificationsIcon />}>
                Push notifications will be sent to iOS devices
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSendNotification}
            startIcon={<SendIcon />}
            disabled={createNotificationMutation.isLoading}
          >
            {createNotificationMutation.isLoading ? 'Sending...' : 'Send Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsDetailsView;